'use client';

import { useEffect } from 'react';
import { report } from '~/lib/exception-handler';
import { handleChunkLoadError } from '~/lib/chunk-load-error';

// Route-segment error boundary. Catches errors thrown by pages and nested
// segments without unmounting the root layout, so the user keeps navigation
// and the header/footer chrome.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // #766 — a chunk that vanished under a deploy is fixed by one reload, not
    // by an incident. Guarded against loops in handleChunkLoadError.
    if (handleChunkLoadError(error)) return;
    report(error, {
      filename: 'app/error.tsx',
      method: 'RouteSegmentError',
      tags: error.digest ? { digest: error.digest } : undefined,
    });
  }, [error]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
      <h1 className="text-2xl font-semibold text-gray-900">
        Something went wrong
      </h1>
      <p className="text-sm text-gray-600">
        We&apos;ve been notified. You can try again, or continue browsing.
      </p>
      <button
        type="button"
        onClick={reset}
        className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
      >
        Try again
      </button>
    </div>
  );
}
